import { useState, useMemo } from "react";
import { PageHeader } from "../molecules/PageHeader";
import { StatsGrid } from "../organisms/StatsGrid";
import { DataTable } from "../organisms/DataTable";
import { SearchInput } from "../atoms/SearchInput";
import { FilterButton } from "../atoms/FilterButton";
import { More } from "iconsax-react";

import {
  MESSAGES_STATS,
  MESSAGESNOTIFICATIONS_TABLE_HEADERS,
  MOCK_MESSAGES,
  type MessagesType,
  BROADCAST_TABLE_HEADERS,
  MOCK_BROADCASTS,
  type Broadcast,
  type Notification,
  NOTIFICATIONS_TABLE_HEADERS,
  MOCK_NOTIFICATIONS,
} from "@/data/messages.data";

const MessagesTableRow = ({ message }: { message: MessagesType }) => (
  <tr className="border-b border-dark-30 hover:bg-dark-30 transition-colors">
    <td className="px-6 py-4 text-dark-text font-medium">{message.ID}</td>
    <td className="px-6 py-4 text-dark-text">{message.type}</td>
    <td className="px-6 py-4 text-dark-text">{message.sender}</td>
    <td className="px-6 py-4 text-dark-text">{message.recipient}</td>
    <td className="px-6 py-4 text-dark-text">{message.preview}</td>
    <td className="px-6 py-4 text-dark-text">{message.status}</td>
    <td className="px-6 py-4 text-dark-text">{message.date}</td>
    <td className="px-6 py-4">
      <button className="text-dark-100 hover:text-dark-text transition-colors p-1">
        <More className="w-6 h-6" color="#EEEEEE" />
      </button>
    </td>
  </tr>
);

const BroadcastTableRow = ({ broadcast }: { broadcast: Broadcast }) => (
  <tr className="border-b border-dark-30 hover:bg-dark-30 transition-colors">
    <td className="px-6 py-4 text-dark-text font-medium">{broadcast.ID}</td>
    <td className="px-6 py-4 text-dark-text">{broadcast.channel}</td>
    <td className="px-6 py-4 text-dark-text">{broadcast.audience}</td>
    <td className="px-6 py-4 text-dark-text">{broadcast.preview}</td>
    <td className="px-6 py-4 text-dark-text">{broadcast.status}</td>
    <td className="px-6 py-4 text-dark-text">{broadcast.date}</td>
    <td className="px-6 py-4">
      <button className="text-dark-100 hover:text-dark-text transition-colors p-1">
        <More className="w-6 h-6" color="#EEEEEE" />
      </button>
    </td>
  </tr>
);

const NotificationTableRow = ({
  notification,
}: {
  notification: Notification;
}) => (
  <tr className="border-b border-dark-30 hover:bg-dark-30 transition-colors">
    <td className="px-6 py-4 text-dark-text font-medium">{notification.ID}</td>
    <td className="px-6 py-4 text-dark-text">{notification.channel}</td>
    <td className="px-6 py-4 text-dark-text">{notification.event}</td>
    <td className="px-6 py-4 text-dark-text">{notification.preview}</td>
    <td className="px-6 py-4 text-dark-text">{notification.status}</td>
    <td className="px-6 py-4">
      <button className="text-dark-100 hover:text-dark-text transition-colors p-1">
        <More className="w-6 h-6" color="#EEEEEE" />
      </button>
    </td>
  </tr>
);

const Messages = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Filter users based on search term
  const filteredMessages = useMemo(() => {
    if (!searchTerm) return MOCK_MESSAGES;
    return MOCK_MESSAGES.filter((message) =>
      message.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  const filteredBroadcasts = useMemo(() => {
    if (!searchTerm) return MOCK_BROADCASTS;
    return MOCK_BROADCASTS.filter((broadcast) =>
      broadcast.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  const filteredNotifications = useMemo(() => {
    if (!searchTerm) return MOCK_NOTIFICATIONS;
    return MOCK_NOTIFICATIONS.filter((notification) =>
      notification.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  return (
    <div className="max-w-7xl mx-auto space-y-10">
      <PageHeader title="Messaging & Notifications Control" />

      <StatsGrid stats={MESSAGES_STATS} columns={3} />

      <DataTable
        title="Messages & Notifications Table"
        headers={MESSAGESNOTIFICATIONS_TABLE_HEADERS}
        data={filteredMessages}
        renderRow={(message) => (
          <MessagesTableRow key={message.id} message={message} />
        )}
        filters={
          <>
            <FilterButton label="Content Type" />
            <FilterButton label="Status" />
            <FilterButton label="Date Range" />
            <FilterButton label="Channel" />
            <SearchInput value={searchTerm} onChange={setSearchTerm} />
          </>
        }
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
      <DataTable
        title="Broadcast Management"
        headers={BROADCAST_TABLE_HEADERS}
        data={filteredBroadcasts}
        renderRow={(broadcast) => (
          <BroadcastTableRow key={broadcast.id} broadcast={broadcast} />
        )}
        filters={
          <>
            <FilterButton label="Status" />
            <FilterButton label="Date Range" />
            <FilterButton label="Channel" />
            <SearchInput value={searchTerm} onChange={setSearchTerm} />
          </>
        }
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
      <DataTable
        title="Notification Rules"
        headers={NOTIFICATIONS_TABLE_HEADERS}
        data={filteredNotifications}
        renderRow={(notification) => (
          <NotificationTableRow
            key={notification.id}
            notification={notification}
          />
        )}
        filters={
          <>
            <FilterButton label="Status" />
            <FilterButton label="Event Trigger" />
            <FilterButton label="Date Range" />
            <FilterButton label="Channel" />
            <SearchInput value={searchTerm} onChange={setSearchTerm} />
          </>
        }
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default Messages;
